<script setup>
import { useHubCardHearts } from '../../../../hub/useHubCardHearts.js';

const skeletonCardRows = [1, 2, 3, 4, 5, 6];

const props = defineProps({
    communities: {
        type: Array,
        required: true,
    },
    isLoading: {
        type: Boolean,
        required: true,
    },
    error: {
        type: String,
        default: '',
    },
    hasEmptyState: {
        type: Boolean,
        required: true,
    },
    formatNumber: {
        type: Function,
        required: true,
    },
    formatCommunityPrice: {
        type: Function,
        required: true,
    },
    resolveThumbnail: {
        type: Function,
        required: true,
    },
});

const { heartBusy, isHeartSaved, toggleHeart } = useHubCardHearts(
    () => props.communities,
    'community',
);
</script>

<template>
    <div v-if="isLoading" class="city-results__cards" role="status" aria-live="polite" aria-label="Loading communities">
        <article
            v-for="row in skeletonCardRows"
            :key="`skeleton-${row}`"
            class="community-card"
            aria-hidden="true"
        >
            <div class="community-card__media">
                <div class="hoa-photo-frame skeleton skeleton-media"></div>
            </div>
            <div class="community-card__body">
                <div class="community-card__metrics">
                    <div class="community-card__pills">
                        <div class="skeleton skeleton-pill"></div>
                        <div class="skeleton skeleton-pill"></div>
                        <div class="skeleton skeleton-pill"></div>
                    </div>
                </div>
                <div class="community-card__headline">
                    <div class="skeleton skeleton-address"></div>
                </div>
                <div class="community-card__price-row">
                    <div class="skeleton skeleton-price"></div>
                </div>
                <div class="community-card__meta">
                    <div class="skeleton skeleton-meta"></div>
                </div>
                <div class="community-card__copy-row">
                    <div class="skeleton skeleton-copy"></div>
                </div>
                <div class="community-card__footer mt-2">
                    <div class="flex gap-1">
                        <div class="skeleton skeleton-tag"></div>
                        <div class="skeleton skeleton-tag"></div>
                    </div>
                    <div class="skeleton skeleton-link"></div>
                </div>
            </div>
        </article>
    </div>
    <p v-else-if="error" class="hoa-text-xs-bold mt-4" role="alert">{{ error }}</p>
    <p v-else-if="hasEmptyState" class="hoa-text-xs-bold font-semibold mt-4" role="status">No communities found.</p>

    <div v-else class="city-results__cards" role="list">
        <article
            v-for="community in communities"
            :key="community.id"
            class="community-card"
            role="listitem"
            :aria-labelledby="`community-search-card-title-${community.id}`"
        >
            <div class="community-card__media">
                <a :href="community.url" :aria-label="`View ${community.name} details`" :title="`${community.name}, Homes for Sale and Rent`">
                    <div class="hoa-photo-frame">
                        <img class="hoa-photo-frame__img" :src="resolveThumbnail(community.thumbnail_url)" :alt="community.name" loading="lazy">
                    </div>
                </a>
                <span class="community-card__availability hoa-text-xs">
                    {{ `${formatNumber(community.homes_available)} ${community.homes_available === 0 || community.homes_available === 1 ? 'Home' : 'Homes'} Available` }}
                </span>
            </div>
            <div class="community-card__body">
                <div class="community-card__metrics">
                    <div class="community-card__pills">
                        <span class="community-card__pill hoa-text-xs-bold">
                            <span>{{ formatNumber(community.avg_sqft) }}</span>
                            <span class="community-card__pill-label">avg. sqft.</span>
                        </span>
                        <span class="community-card__pill hoa-text-xs-bold">
                            <span>{{ formatNumber(community.avg_lot_sqft) }}</span>
                            <span class="community-card__pill-label">avg. lot sqft</span>
                        </span>
                        <span class="community-card__pill hoa-text-xs-bold">
                            <span>{{ formatNumber(community.total_homes) }}</span>
                            <span class="community-card__pill-label">total homes</span>
                        </span>
                    </div>
                    <button
                        type="button"
                        class="community-card__favorite"
                        :class="{ 'is-saved': isHeartSaved(community.id) }"
                        :aria-pressed="isHeartSaved(community.id)"
                        :aria-label="isHeartSaved(community.id) ? `Unsave ${community.name}` : `Save ${community.name}`"
                        :disabled="Boolean(heartBusy[community.id])"
                        @click.stop="toggleHeart(community)"
                    >
                        <span class="material-symbols-outlined" aria-hidden="true">favorite</span>
                    </button>
                </div>
                <div class="community-card__headline">
                    <a :href="community.url" class="community-card__headline-link" :title="`${community.name}, Homes for Sale and Rent`">
                        <h2 :id="`community-search-card-title-${community.id}`" class="hoa-text-sm-bold">{{ community.name }}</h2>
                    </a>
                </div>
                <p class="hoa-text-xs-bold community-card__price">
                    {{ formatCommunityPrice(community.starting_price_rentals, community.starting_price_sales) }}
                </p>
                <div
                    v-if="Array.isArray(community.badges) && community.badges.length"
                    class="community-card__meta hoa-10px"
                    role="list"
                    aria-label="Community badges"
                >
                    <template v-for="(badge, badgeIndex) in community.badges" :key="`${community.id}-badge-${badgeIndex}`">
                        <div class="community-card__meta-item" role="listitem">
                            <span class="material-symbols-outlined" style="color: var(--hoa-medium-grey)" aria-hidden="true">construction</span>
                            <span>{{ badge }}</span>
                        </div>
                    </template>
                </div>
                <p class="hoa-10px font-medium community-card__copy">{{ community.description }}</p>
                <div class="community-card__footer mt-2">
                    <div class="flex gap-1 flex-wrap" role="list" aria-label="Property types">
                        <span
                            v-for="(propertyType, typeIndex) in community.property_types || []"
                            :key="`${community.id}-type-${typeIndex}`"
                            class="community-card__tag hoa-text-xs-bold"
                            role="listitem"
                        >
                            {{ propertyType }}
                        </span>
                    </div>
                    <a class="hoa-10px underline community-card__link" :href="community.url" :title="`${community.name}, Homes for Sale and Rent`">
                        See Details
                    </a>
                </div>
            </div>
        </article>
    </div>
</template>
<style scoped>
.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.city-results__cards {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.skeleton-pill {
    width: 120px;
    height: 22px;
    border-radius: 999px;
}

.skeleton-meta {
    width: 40%;
    height: 14px;
    margin-top: 8px;
}

.skeleton-copy {
    width: 100%;
    height: 30px;
    margin-top: 8px;
}

.skeleton-link {
    width: 70px;
    height: 12px;
}

.community-card__price-row {
    margin-top: 8px;
}

.community-card__copy-row {
    margin-top: 8px;
}

.skeleton-copy,
.skeleton-link,
.skeleton-meta,
.skeleton-pill,
.skeleton-tag,
.skeleton-address,
.skeleton-price,
.skeleton-media {
    background: linear-gradient(90deg, #efefef 25%, #f7f7f7 37%, #efefef 63%);
    background-size: 400% 100%;
    animation: skeleton-loading 1.4s ease infinite;
    border-radius: 8px;
}

.skeleton-media {
    width: 100%;
}

.skeleton-price {
    width: 180px;
    height: 20px;
}

.skeleton-address {
    width: 60%;
    height: 18px;
    margin-top: 12px;
}

.skeleton-tag {
    width: 76px;
    height: 22px;
    border-radius: 999px;
}

@keyframes skeleton-loading {
    0% {
        background-position: 100% 0;
    }
    100% {
        background-position: 0 0;
    }
}
</style>